import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import {
  DictionaryTermApiProvider,
  useDictionaryTermApi,
} from '../Services/DictionaryTermApiProvider';
import { formatDateUTC } from '../utils/formatDate';

const DictionaryTermsContent = () => {
  const { organizationId, dictionaryId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [error, setError] = useState(null);
  const [terms, setTerms] = useState([]);
  const [totalTerms, setTotalTerms] = useState(0);
  const [termsPerPage, setTermsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const { getDictionaryTerms, deleteDictionaryTerm } = useDictionaryTermApi();

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Keyword', selector: (row) => row.keyword, sortable: true },
    { name: 'Definition', selector: (row) => row.definition, sortable: false },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: false },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
  ];

  const fetchTerms = async (skip = 0, limit = termsPerPage) => {
    if (limit !== termsPerPage) {
      setTermsPerPage(limit);
    }
    setLoading(true);
    try {
      const response = await getDictionaryTerms(dictionaryId, skip, limit);
      setTerms(response.items);
      setTotalTerms(response.totalCount);
    } catch (err) {
      console.error('Error fetching dictionary items:', err);
      setError(err.message || 'Error loading terms.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms(0, termsPerPage);
  }, []);

  const handleDelete = (selectedId) => {
    if (!selectedId) return;
    showConfirmation('Are you sure you want to delete this term?', async () => {
      try {
        setLoading(true);
        await deleteDictionaryTerm(dictionaryId, selectedId);
        showAlert('success', 'Term was successfully deleted!');
        fetchTerms(0, termsPerPage);
      } catch {
        showAlert('fail', 'Failed to delete the term');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleRowClicked = (row) => {
    navigate(
      `/organizations/${organizationId}/taxonomy-dictionary/${dictionaryId}/terms/${row.id}`,
    );
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div className="text-danger">Error: {error}</div>
          ) : (
            <Table
              title="Terms"
              columns={columns}
              data={terms}
              selectableRows
              selectableRowsHighlight
              progressPending={loading}
              paginationPerPage={termsPerPage}
              paginationTotalRows={totalTerms}
              onRowClicked={handleRowClicked}
              sortIcon={<i className="fa-solid fa-chevron-down" />}
              onDelete={handleDelete}
              onChangePage={fetchTerms}
            />
          )}
          <Button
            background="color2"
            size="large"
            onClick={() =>
              navigate(
                `/organizations/${organizationId}/taxonomy-dictionary/${dictionaryId}/terms/new`,
              )
            }
          >
            Add Term
          </Button>
        </div>
      </div>
    </div>
  );
};
function DictionaryTerms({ canEdit }) {
  return (
    <ConfirmationProvider>
      <DictionaryTermApiProvider>
        <DictionaryTermsContent canEdit={canEdit} />
      </DictionaryTermApiProvider>
    </ConfirmationProvider>
  );
}

export default DictionaryTerms;
